import { ImgID } from "../../../image_loader/img_id.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import type { PrizeShotReward, PrizeShotRound } from "../../../prize_shot/prize_shot_rules.js";
import { Vector2 } from "../../../common/vector2.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { prizeShotCoinImageBounds } from "../../../prize_shot/prize_shot_coin_art.js";

export interface PrizesTooltipAnchor
{
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * "You can win": every prize of the round, the found ones ticked off. Drawn
 * like the Mystery Box info tooltip: a nine-slice bubble and its nose.
 */
export class PrizeShotPrizesTooltip extends UIElementHolder
{
  private static readonly DESIGN_W = 940;
  private static readonly DESIGN_H = 523;
  private static readonly TAIL_H = 78;
  /** The nose tucks this far under the bubble's frame. */
  private static readonly TAIL_OVERLAP = 32;
  private static readonly ITEMS_X = 70;
  private static readonly APPEAR_SCALE = 0.85;

  private readonly round: PrizeShotRound;
  private readonly tail_up: boolean;
  private readonly tail_x: number;
  private readonly bubble_w: number;
  private readonly bubble_h: number;

  /** Above the anchor with the nose pointing down at it, or under it where there is no room above. */
  public static createAt( ctx: CanvasRenderingContext2D, round: PrizeShotRound, anchor: PrizesTooltipAnchor, area_w: number ): PrizeShotPrizesTooltip
  {
    const w = LayoutScaler.widthFromDesign( PrizeShotPrizesTooltip.DESIGN_W );
    const h = LayoutScaler.heightFromDesign( PrizeShotPrizesTooltip.DESIGN_H ) + PrizeShotPrizesTooltip.tailOut();
    const margin = LayoutScaler.widthFromDesign( 20 );
    const anchor_center_x = anchor.x + anchor.w / 2;

    const x = Math.max( margin, Math.min( anchor_center_x - w / 2, area_w - w - margin ) );
    const above_y = anchor.y - h + LayoutScaler.heightFromDesign( 10 );
    const below_y = anchor.y + anchor.h + LayoutScaler.heightFromDesign( 4 );
    const tail_up = above_y < margin;

    const tooltip = new PrizeShotPrizesTooltip( ctx, x, tail_up ? below_y : above_y, round, tail_up, anchor_center_x - x );
    tooltip.playAppear();
    return tooltip;
  }

  private static tailOut(): number
  {
    return LayoutScaler.heightFromDesign( PrizeShotPrizesTooltip.TAIL_H - PrizeShotPrizesTooltip.TAIL_OVERLAP );
  }

  private constructor( ctx: CanvasRenderingContext2D, x: number, y: number, round: PrizeShotRound, tail_up: boolean, tail_x: number )
  {
    const bubble_w = LayoutScaler.widthFromDesign( PrizeShotPrizesTooltip.DESIGN_W );
    const bubble_h = LayoutScaler.heightFromDesign( PrizeShotPrizesTooltip.DESIGN_H );

    super( ctx, x, y, bubble_w, bubble_h + PrizeShotPrizesTooltip.tailOut() );

    this.round = round;
    this.tail_up = tail_up;
    this.tail_x = tail_x;
    this.bubble_w = bubble_w;
    this.bubble_h = bubble_h;

    this.createElements();
  }

  private createElements(): void
  {
    this.createBubble();
    this.createTail();
  }

  private createBubble(): void
  {
    const bubble_y = this.tail_up ? PrizeShotPrizesTooltip.tailOut() : 0;

    const bubble = new UIElementHolder( this.ctx, 0, bubble_y, this.bubble_w, this.bubble_h );
    bubble.addChild( new UINineSliceElement(
      this.ctx,
      0,
      0,
      this.bubble_w,
      this.bubble_h,
      ImgID.UI_TutorialSubstrate,
      "tutorial_substrate"
    ) );

    this.createTitle( bubble );
    this.createPrizeItems( bubble );

    // Taps on any part reach the tooltip itself, so its owner's onClick closes it.
    this.addChild( bubble, true );
  }

  private createTitle( bubble: UIElementHolder ): void
  {
    const design_title_y = 40;
    const design_title_h = 70;
    const design_font_size = 45;
    const design_outline_w = 5;

    const title = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.heightFromDesign( design_title_y ),
      this.bubble_w,
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: "PRIZE_SHOT.YOU_CAN_WIN",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: "#2f4b87",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.white,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    bubble.addChild( title );
  }

  private createPrizeItems( bubble: UIElementHolder ): void
  {
    const found = new Set( this.round.popped );
    let index = 0;

    this.round.board.forEach( ( reward, cell ) =>
    {
      if ( !reward )
        return;

      this.createPrizeItem( bubble, reward, found.has( cell ), index );
      index++;
    } );
  }

  private createPrizeItem( bubble: UIElementHolder, reward: PrizeShotReward, found: boolean, index: number ): void
  {
    const columns = 4;
    const design_item_w = 145;
    const design_step_x = ( PrizeShotPrizesTooltip.DESIGN_W - PrizeShotPrizesTooltip.ITEMS_X * 2 - design_item_w ) / ( columns - 1 );
    const design_item_x = PrizeShotPrizesTooltip.ITEMS_X + ( index % columns ) * design_step_x;
    const design_item_y = 103 + Math.floor( index / columns ) * 125;
    const design_text_y = 72;
    const design_text_h = 46;
    const design_font_size = 32;
    const design_outline_w = 9;
    const design_check_size = 62;
    const design_check_y = -14;

    const item_w = LayoutScaler.widthFromDesign( design_item_w );
    const item_h = LayoutScaler.heightFromDesign( 110 );

    const item = new UIElementHolder(
      this.ctx,
      LayoutScaler.widthFromDesign( design_item_x ),
      LayoutScaler.heightFromDesign( design_item_y ),
      item_w,
      item_h
    );
    bubble.addChild( item );

    const is_coin = reward.type === "coin";
    const icon_w = is_coin ? item_w : item_h;
    const icon_bounds = is_coin
      ? prizeShotCoinImageBounds( ( item_w - icon_w ) / 2, 0, icon_w, item_h )
      : { x: ( item_w - icon_w ) / 2, y: 0, width: icon_w, height: item_h };

    const icon = new UIImage(
      this.ctx,
      icon_bounds.x,
      icon_bounds.y,
      icon_bounds.width,
      icon_bounds.height,
      is_coin ? ImgID.UI_OnePlusOneOfferCoins : ADVENTURE_REWARD_ICONS[reward.type]
    );
    item.addChild( icon );

    const amount = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.heightFromDesign( design_text_y ),
      item_w,
      LayoutScaler.heightFromDesign( design_text_h ),
      {
        text: is_coin ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount ),
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: "#003f87",
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        auto_wrap: false,
      }
    );
    item.addChild( amount );

    if ( !found )
      return;

    icon.setOpacityImmediate( 0.4 );
    amount.setOpacityImmediate( 0.4 );

    const check_size = LayoutScaler.widthFromDesign( design_check_size );
    const check = new UIImage(
      this.ctx,
      ( item_w - check_size ) / 2,
      LayoutScaler.heightFromDesign( design_check_y ),
      check_size,
      check_size,
      ImgID.UI_TournamentCheck
    );
    item.addChild( check );
  }

  private createTail(): void
  {
    const tail_w = LayoutScaler.widthFromDesign( 111 );
    const tail_h = LayoutScaler.heightFromDesign( PrizeShotPrizesTooltip.TAIL_H );
    const edge = LayoutScaler.widthFromDesign( 80 );
    const tail_x = Math.max( edge, Math.min( this.bubble_w - edge, this.tail_x ) ) - tail_w / 2;
    const tail_y = this.tail_up
      ? 0
      : this.bubble_h - LayoutScaler.heightFromDesign( PrizeShotPrizesTooltip.TAIL_OVERLAP );

    // tooltip_edge.png points up; under the bubble it is turned over.
    const tail = new UIImage(
      this.ctx,
      tail_x,
      tail_y,
      tail_w,
      tail_h,
      ImgID.UI_MysteryBoxTooltipEdge,
      this.tail_up ? undefined : { rotation_angle: 180 }
    );
    this.addChild( tail, true );
  }

  private playAppear(): void
  {
    const from = new Vector2( PrizeShotPrizesTooltip.APPEAR_SCALE, PrizeShotPrizesTooltip.APPEAR_SCALE );

    this.setScaleImmediate( from );
    this.addTransform( new TransformScale(
      this,
      true,
      from,
      new Vector2( 1, 1 ),
      performance.now(),
      140,
      { easing: Easing.EaseOutQuad }
    ) );
  }
}
